/** 매장리스트 캐러셀 */
'use client';

import BranchCarouselCard from '@/components/branch-carousel-card';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { StoreEntity } from '@/services/types/store.type';
import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useState } from 'react';

interface UnlimitedBranchCarouselProps {
  branches: StoreEntity[];
}

const UnlimitedBranchCarousel = ({
  branches,
}: UnlimitedBranchCarouselProps) => {
  const [pcApi, setPcApi] = useState<CarouselApi>();
  const [mobileApi, setMobileApi] = useState<CarouselApi>();
  const [pcCurrentSlide, setPcCurrentSlide] = useState(0);
  const [mobileCurrentSlide, setMobileCurrentSlide] = useState(0);

  useEffect(() => {
    if (!mobileApi && !pcApi) return;

    const cleanups: (() => void)[] = [];

    if (mobileApi) {
      const onMobileSelect = () => {
        setMobileCurrentSlide(mobileApi.selectedScrollSnap());
      };
      mobileApi.on('select', onMobileSelect);
      onMobileSelect();
      cleanups.push(() => mobileApi.off('select', onMobileSelect));
    }

    if (pcApi) {
      const onPcSelect = () => {
        setPcCurrentSlide(pcApi.selectedScrollSnap());
      };
      pcApi.on('select', onPcSelect);
      onPcSelect();
      cleanups.push(() => pcApi.off('select', onPcSelect));
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [mobileApi, pcApi]);

  const handlePrevClick = () => {
    pcApi?.scrollPrev();
    const autoplay = pcApi?.plugins()?.autoplay;
    if (!autoplay || typeof autoplay.play !== 'function') return;
    autoplay.stop();
    setTimeout(() => {
      if (autoplay && typeof autoplay.play === 'function') {
        autoplay.play();
      }
    }, 1000);
  };

  const handleNextClick = () => {
    pcApi?.scrollNext();
    const autoplay = pcApi?.plugins()?.autoplay;
    if (!autoplay || typeof autoplay.play !== 'function') return;
    autoplay.stop();
    setTimeout(() => {
      if (autoplay && typeof autoplay.play === 'function') {
        autoplay.play();
      }
    }, 1000);
  };

  const handleMobilePrevClick = () => {
    mobileApi?.scrollPrev();
    const autoplay = mobileApi?.plugins()?.autoplay;
    if (!autoplay || typeof autoplay.play !== 'function') return;
    autoplay.stop();
    setTimeout(() => {
      if (autoplay && typeof autoplay.play === 'function') {
        autoplay.play();
      }
    }, 1000);
  };

  const handleMobileNextClick = () => {
    mobileApi?.scrollNext();
    const autoplay = mobileApi?.plugins()?.autoplay;
    if (!autoplay || typeof autoplay.play !== 'function') return;
    autoplay.stop();
    setTimeout(() => {
      if (autoplay && typeof autoplay.play === 'function') {
        autoplay.play();
      }
    }, 1000);
  };

  if (!branches || branches.length === 0) {
    return null;
  }

  return (
    <>
      {/* 웹 */}
      <div className="desktop:block hidden">
        <div className="relative mx-auto">
          <div className="relative">
            <Carousel
              setApi={setPcApi}
              opts={{
                align: 'center',
                loop: true,
                slidesToScroll: 2,
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                  stopOnMouseEnter: true,
                  stopOnInteraction: false,
                }),
              ]}
            >
              <CarouselContent className="mx-auto -ml-[16px]">
                {branches.map((branch, idx) => {
                  const isVisible =
                    idx === pcCurrentSlide * 2 ||
                    idx === (pcCurrentSlide * 2 + 1) % branches.length;
                  return (
                    <CarouselItem
                      key={branch.id}
                      className={cn(
                        'desktop:w-[600px] desktop:flex-shrink-0 desktop:basis-[600px] h-auto',
                        isVisible ? 'opacity-100' : 'opacity-50',
                      )}
                    >
                      <BranchCarouselCard branch={branch} />
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>
          </div>

          <div className="web-w relative mx-auto">
            {/* 좌측 화살표 버튼 */}
            <button
              onClick={handlePrevClick}
              disabled={!pcApi}
              className="desktop:size-12 desktop:left-4 desktop:-ml-[42px] absolute -top-[110px] left-2 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="이전 슬라이드"
            >
              <div className="desktop:size-12 desktop:rounded-full flex size-10 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="desktop:size-6 text-main-orange size-5"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>

            {/* 우측 화살표 버튼 */}
            <button
              onClick={handleNextClick}
              disabled={!pcApi}
              className="desktop:size-12 desktop:right-4 desktop:-mr-[42px] absolute -top-[110px] right-2 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="다음 슬라이드"
            >
              <div className="desktop:size-12 desktop:rounded-full flex size-10 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="desktop:size-6 text-main-orange size-5"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* 모바일 */}
      <section className="desktop:hidden overflow-hidden">
        <Carousel
          setApi={setMobileApi}
          opts={{
            loop: true,
            align: 'center',
            slidesToScroll: 1,
            containScroll: 'trimSnaps',
          }}
          plugins={[
            Autoplay({
              delay: 3000,
              stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
        >
          <CarouselContent className="mx-auto">
            {branches.map((branch, idx) => (
              <CarouselItem
                key={branch.id}
                className={cn(
                  'h-auto w-[calc(100%-60px)] flex-shrink-0 basis-[calc(100%-60px)]',
                  'pl-0',
                  idx === mobileCurrentSlide ? 'opacity-100' : 'opacity-50',
                )}
              >
                <BranchCarouselCard branch={branch} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="web-w relative mx-auto">
          {/* 좌측 화살표 버튼 */}
          <button
            onClick={handleMobilePrevClick}
            disabled={!mobileApi}
            className="desktop:size-12 desktop:left-4 desktop:-ml-[42px] absolute -top-[80px] left-2 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="이전 슬라이드"
          >
            <div className="desktop:size-12 desktop:rounded-full flex size-8 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="desktop:size-6 text-main-orange size-5"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>

          {/* 우측 화살표 버튼 */}
          <button
            onClick={handleMobileNextClick}
            disabled={!mobileApi}
            className="desktop:size-12 desktop:right-4 desktop:-mr-[42px] absolute -top-[80px] right-2 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="다음 슬라이드"
          >
            <div className="desktop:size-12 desktop:rounded-full flex size-8 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="desktop:size-6 text-main-orange size-5"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
      </section>
    </>
  );
};

export default UnlimitedBranchCarousel;
